import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMemberAuth } from '../contexts/MemberAuthContext';
import { useLang } from '../contexts/LanguageContext';
import api from '../api/axios';
import { LogIn, Shield, Loader2 } from 'lucide-react';
import logo from '../assets/styles/logo.png';
import bgImage from '../assets/styles/variables/1233456.jpg';
import LangSwitch from '../components/common/LangSwitch';

export default function Login() {
  const { login, user } = useAuth();
  const memberAuth = useMemberAuth();
  const { lang } = useLang();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Init (first-time setup) state
  const [showInit, setShowInit] = useState(false);
  const [initEmail, setInitEmail] = useState('');
  const [initPassword, setInitPassword] = useState('');
  const [initLoading, setInitLoading] = useState(false);

  // Passwort vergessen state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setForgotLoading(true);
    try {
      await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(err.response?.data?.error || (lang === 'tr' ? 'Bir hata oluştu' : 'Ein Fehler ist aufgetreten'));
    } finally {
      setForgotLoading(false);
    }
  };

  // Redirect if already logged in (Admin -> /admin, Mitglied -> /uye)
  React.useEffect(() => {
    if (user) navigate('/admin', { replace: true });
    else if (memberAuth?.member) navigate('/uye', { replace: true });
  }, [user, memberAuth?.member, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    // 1) Yonetici / personel girisi
    try {
      await login(email, password);
      navigate('/admin');
      return;
    } catch (adminErr) {
      // 2) Yonetici basarisizsa uye girisi dene
      try {
        if (memberAuth?.login) {
          await memberAuth.login(email, password);
          navigate('/uye');
          return;
        }
      } catch (memberErr) {
        // ikisi de basarisiz
      }
      // Kurulum (init) veya hata mesaji
      const msg = adminErr.response?.data?.error || adminErr.response?.data?.message || (lang === 'tr' ? 'Giriş başarısız' : 'Anmeldung fehlgeschlagen');
      if (
        msg.toLowerCase().includes('init') ||
        msg.toLowerCase().includes('kurulum') ||
        adminErr.response?.status === 503
      ) {
        setShowInit(true);
        setError(lang === 'tr' ? 'Sistem henüz kurulmadı. Lütfen bir yönetici hesabı oluşturun.' : 'Das System wurde noch nicht eingerichtet. Bitte erstellen Sie ein Administratorkonto.');
      } else {
        setError(lang === 'tr' ? 'E-posta veya şifre hatalı' : 'E-Mail oder Passwort ist falsch');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInit = async (e) => {
    e.preventDefault();
    setError('');
    setInitLoading(true);
    try {
      await api.post('/auth/init', {
        email: initEmail,
        password: initPassword,
      });
      await login(initEmail, initPassword);
      navigate('/admin');
    } catch (err) {
      setError(err.response?.data?.message || (lang === 'tr' ? 'Kurulum başarısız' : 'Einrichtung fehlgeschlagen'));
    } finally {
      setInitLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 relative" style={{ backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
      <div className="absolute inset-0 bg-black/50"></div>
      <div className="absolute top-4 right-4 z-20"><LangSwitch /></div>
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo / Title */}
          <div className="text-center mb-8">
            <img src={logo} alt="Galatasaray Gelsenkirchen e.V." className="mx-auto w-24 h-24 object-contain mb-4" />
            <h1 className="text-2xl font-bold text-gray-900">
              Galatasaray Gelsenkirchen e.V.
            </h1>
            <p className="text-gray-500 mt-1 text-sm">
              {lang === 'tr' ? 'Yönetim Paneli' : 'Verwaltungspanel'}
            </p>
          </div>

          {/* Error message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {showForgot ? (
            /* Passwort vergessen Form */
            <form onSubmit={handleForgot} className="space-y-4">
              {forgotSent ? (
                <div className="text-center space-y-4">
                  <p className="text-sm text-gray-600">
                    {lang === 'tr' ? 'Bu e-posta ile bir hesap varsa, şifre sıfırlama bağlantısı gönderildi. Lütfen gelen kutunuzu kontrol edin.' : 'Falls ein Konto mit dieser E-Mail existiert, wurde ein Link zum Zurücksetzen des Passworts gesendet. Bitte prüfen Sie Ihr Postfach.'}
                  </p>
                  <button type="button" onClick={() => { setShowForgot(false); setForgotSent(false); setError(''); }} className="text-sm text-gs-red font-medium hover:underline">
                    {lang === 'tr' ? 'Giriş Sayfasına Dön' : 'Zurück zur Anmeldung'}
                  </button>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-600">
                    {lang === 'tr' ? 'E-posta adresinizi girin. Size şifrenizi sıfırlamanız için bir bağlantı göndereceğiz.' : 'Geben Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.'}
                  </p>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{lang === 'tr' ? 'E-posta' : 'E-Mail'}</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      required
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gs-yellow focus:border-gs-yellow outline-none transition-colors"
                      placeholder="beispiel@email.com"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full flex items-center justify-center gap-2 bg-gs-red text-white py-3 min-h-[48px] rounded-lg font-medium hover:bg-gs-red/90 transition-colors disabled:opacity-50"
                  >
                    {forgotLoading ? <Loader2 size={20} className="animate-spin" /> : <LogIn size={20} />}
                    {forgotLoading ? (lang === 'tr' ? 'Gönderiliyor...' : 'Wird gesendet...') : (lang === 'tr' ? 'Bağlantı Gönder' : 'Link senden')}
                  </button>
                  <button type="button" onClick={() => { setShowForgot(false); setError(''); }} className="w-full text-sm text-gray-500 hover:text-gray-700">
                    {lang === 'tr' ? 'Giriş Sayfasına Dön' : 'Zurück zur Anmeldung'}
                  </button>
                </>
              )}
            </form>
          ) : !showInit ? (
            /* Login Form */
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {lang === 'tr' ? 'E-posta' : 'E-Mail'}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gs-yellow focus:border-gs-yellow outline-none transition-colors"
                  placeholder="beispiel@email.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {lang === 'tr' ? 'Şifre' : 'Passwort'}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gs-yellow focus:border-gs-yellow outline-none transition-colors"
                  placeholder={lang === 'tr' ? 'Şifre girin' : 'Passwort eingeben'}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 bg-gs-red text-white py-3 min-h-[48px] rounded-lg font-medium hover:bg-gs-red/90 focus:ring-2 focus:ring-gs-yellow focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <LogIn size={20} />
                )}
                {loading ? (lang === 'tr' ? 'Giriş Yap...' : 'Anmelden...') : (lang === 'tr' ? 'Giriş Yap' : 'Anmelden')}
              </button>

              <button
                type="button"
                onClick={() => { setShowForgot(true); setError(''); }}
                className="w-full text-center text-sm text-gs-yellow hover:text-gs-yellow/80 font-medium"
              >
                {lang === 'tr' ? 'Şifremi Unuttum?' : 'Passwort vergessen?'}
              </button>
            </form>
          ) : (
            /* Init Form */
            <form onSubmit={handleInit} className="space-y-4">
              <p className="text-sm text-gray-600 mb-2">
                {lang === 'tr' ? 'İlk kurulum: Yönetici hesabı oluştur' : 'Ersteinrichtung: Administratorkonto erstellen'}
              </p>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {lang === 'tr' ? 'Yönetici E-posta' : 'Administrator E-Mail'}
                </label>
                <input
                  type="email"
                  value={initEmail}
                  onChange={(e) => setInitEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gs-red focus:border-gs-red outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {lang === 'tr' ? 'Yönetici Şifre' : 'Administrator Passwort'}
                </label>
                <input
                  type="password"
                  value={initPassword}
                  onChange={(e) => setInitPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gs-red focus:border-gs-red outline-none transition-colors"
                />
              </div>
              <button
                type="submit"
                disabled={initLoading}
                className="w-full flex items-center justify-center gap-2 bg-green-600 text-white py-3 min-h-[48px] rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {initLoading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <Shield size={20} />
                )}
                {initLoading ? (lang === 'tr' ? 'Kuruluyor...' : 'Wird eingerichtet...') : (lang === 'tr' ? 'Sistemi Kur' : 'System einrichten')}
              </button>
              <button
                type="button"
                onClick={() => setShowInit(false)}
                className="w-full text-sm text-gray-500 hover:text-gray-700"
              >
                {lang === 'tr' ? 'Giriş Sayfasına Dön' : 'Zurück zur Anmeldung'}
              </button>
            </form>
          )}

          {/* Registration link */}
          {!showForgot && (
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-500">
                {lang === 'tr' ? 'Henüz üye değil misiniz?' : 'Noch kein Mitglied?'}{' '}
                <Link
                  to="/kayit"
                  className="text-blue-600 hover:text-blue-700 font-medium"
                >
                  {lang === 'tr' ? 'Üyelik Başvurusu' : 'Mitgliedsbewerbung'}
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
